/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { MessageService } from '@src/app/shared/message.service';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { Subscription } from 'rxjs';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { Role } from '@src/app/model/role';
import { GridField } from '@src/app/shared/grid/grid-field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { EntityTypes, Person } from '@src/app/model/person';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { Field } from '@src/app/shared/field/Field';
import { AbstractObject } from '@src/app/model/abstract-object';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { map } from 'rxjs/operators';
import { required } from '@src/app/shared/validators';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { DialogBannerComponent } from "@src/app/shared/dialogs/dialog-banner-component";
import { NgStyle } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-owner-change',
    imports: [MatDialogModule, FieldSetComponent, MatButtonModule, DialogBannerComponent, NgStyle, MatIconModule, MatButtonModule],
    templateUrl: './owner-change.component.html',
    styleUrl: './owner-change.component.scss'
})
export class OwnerChangeComponent {
    dialogRef = inject(MatDialogRef<OwnerChangeComponent>);
    dialog = inject(MatDialog);
    data = inject(MAT_DIALOG_DATA);
    unit = this.data.unit;
    unitSvc = inject(UnitService);
    peopleSvc = inject(PersonService);
    roleSvc = inject(RoleService);
    msgSvc = inject(MessageService);
    cds = inject(ConfirmDialogService);
    isPhone = false;
    screenSvc = inject(IsNarrowService);
    subscriptions: Subscription[] = [];
    ready = false;
    comment = this.getFirstComment();

    icon = this.data.transfer ? 'transfer_within_a_station' : 'group';

    title = this.data.transfer ?
        $localize `Transfer Unit Ownership for ${this.unit.name}` :
        $localize `Add contact to ${this.unit.name}`

    entityField = FormPicklistComponent.make('Choose Type of Owner to add', 'entityType', null, {
        items: EntityTypes.chooseType,
        refreshes: [this.refreshEntity.bind(this)],
    }, {formColumn: 1, validators: [required]});

    transferDateField = FormDateComponent.make('TransferDate', 'transferDate', {
        hint: $localize `Date the new owner(s) will take ownership`, formColumn: 1,
        validators: [required], valueChanges: this.setTransferDate.bind(this)
    });

    balanceTransferField = FormNumberComponent.makeCurrency('Balance on transfer date', 'balance', {
        hint: $localize `The balance outstanding that will be transferred to new owners account on the date of the transfer`,
        disable: true, sendServer: false
    });

    totalBalanceField = FormNumberComponent.makeCurrency('Total Balance Transferred', 'balance', {
        hint: $localize `Total balance on transfer plus any transactions dated after transfer date which will also be visible on the new owners account`,
        disable: true, sendServer: false
    })

    roleField = FormPicklistComponent.make('Role', 'roleId', null, {items: [], refreshes: [this.setRoleField.bind(this)]}, {
        hint: $localize `Involvement / Residency - If the role is not an owner occupier, then it will be mandatory to enter an address`,
        formColumn: 1, validators: [required]
    });

    noteField = FormTextAreaComponent.make('Note relating to this contact @ this unit', 'notes');

    peopleGrid: GridField = new GridField({
        field: { value: 'owners', formColumn: 2,
            cellOpts: { heading: 'Owner Contacts'}, valueChanges: this.resetFields.bind(this)},
        rowFactory: () => {
            return [
                FieldMaker.id(),
                FieldMaker.idHolder('personId'),
                FormTextComponent.make('Owner Name', 'fullName', {readonly: true}),
                FieldMaker.deleteGridRow()
            ]
        },
        objFactory: this.chooseContact.bind(this)
    });

    titleField = Person.getTitleField().override({visible:Field.noShow, formColumn: 2});
    firstNameField = FormTextComponent.make('First Name', 'firstName', {visible: Field.noShow, formColumn: 2});
    lastNameField = FormTextComponent.make('Last Name', 'lastName', {visible: Field.noShow, formColumn: 2});
    phoneField = FormTextComponent.make('Telephone', 'phone', {formColumn: 2, visible: Field.noShow});
    emailField = FormTextComponent.make('Email', 'email', {formColumn: 2, visible: Field.noShow});
    addressField = FormTextAreaComponent.make('Address', 'address', {formColumn: 2,visible: Field.noShow});

    fields = this.getFields();

    fieldSet = new FieldSet({ formLayout: [{ cells: [{ width: '50%' }, { width: '50%' }] }]});

    constructor() {
        this.subscriptions.push(this.screenSvc.detectVeryNarrow().subscribe(result => { this.isPhone = result }));
        this.fieldSet.setFields(this.fields);
        this.roleSvc.get<Role>(true, null, this.unit.teamId).subscribe( roles => {

            roles.forEach( r => {
                if (this.data.transfer) {
                    if (r.globalRoleId === Role.ROLE.OWNER.id || r.globalRoleId === Role.ROLE.OWNER_OCCUPIER.id) {
                        this.roleField.picklist.items.push(r);
                    }
                } else {
                    if (r.unitLinked) {
                        this.roleField.picklist.items.push(r);
                    }
                }
            })

            this.ready = true;
        })
        this.fieldSet.setValue({owners: [], address: '', phone: '', email: '', title: '', firstName: '', lastName: ''} as unknown as AbstractObject);
        this.resetFields();
    }

    getFirstComment() {
        if (this.data.transfer) {
            return $localize `
                You are changing the owner of this unit.
                The balance outstanding at transfer date should be zero.
                If there is an outstanding balance, a credit note will be generated and
                a new charge created on the transfer date to carry forward to the new owners.
                New Owner(s) will NOT be able to see transactions before the transfer date.
            `;
        }
        return $localize `
            You are adding extra contacts to this unit.
            If the unit has transferred to different ownership you should use the Owner Change action instead.
            Only use this action to add additional contacts to the unit WITHOUT changing ownership.
        `;
    }

    getFields() {
        const fields = [];
        fields.push()
        fields.push(this.entityField);
        fields.push(this.transferDateField);
        if (this.data.transfer) {
            fields.push(this.balanceTransferField);
            fields.push(this.totalBalanceField);
        } else {
            this.transferDateField.label = $localize `Date person associated to unit`;
            fields.push(this.noteField);
        }
        fields.push(this.roleField);
        fields.push(this.peopleGrid);
        fields.push(this.titleField);
        fields.push(this.firstNameField);
        fields.push(this.lastNameField);
        fields.push(this.phoneField);
        fields.push(this.emailField);
        fields.push(this.addressField);
        return fields;
    }

    setTransferDate(e) {
        if (this.data.transfer) {
            this.unitSvc.getBalanceAsAt(this.unit.id, e).subscribe( x => {
                this.balanceTransferField.control.setValue(x.transferBalance, {emitEvent: false});
                this.totalBalanceField.control.setValue(x.totalBalance, {emitEvent: false});
            })
        }
    }

    setRoleField(r: Role) {
        console.log(r);
        if (r.globalRoleId === Role.ROLE.OWNER.id && this.entityField.control.value !== EntityTypes.choose && this.data.transfer) {
            this.addressField.control.setValidators([required]);
        } else {
            console.log('No need for an address', this.entityField.control.value);
            this.addressField.control.clearValidators();
        }
        this.addressField.control.updateValueAndValidity();
        this.fieldSet.formGroup.updateValueAndValidity();
    }

    changeOwner() {
        const title = $localize `Change ownership of unit ${this.unit.name}`;
        const message = $localize `
            Are you sure that you want to update the ownership now?
            After changing the ownership of the unit,
            transactions before the transfer date will no longer be visible on the unit statement.
            The previous owner, unless associated to another unit, will no longer be able to login to the system.
            The new owners, will be able to login to the system, see the transferred balance
            and see transactions after the transfer date. This action cannot be undone.
        `;
        this.cds.open(title,message, () => {
            this.unitSvc.doTransfer(this.unit, this.fieldSet.getFormValue()).subscribe ( response => {
                if (response) {
                    this.dialogRef.close(response);
                }
            }, error => this.msgSvc.showError(error, 'Changing Owner'));
        })
    }

    addContact() {
        const title = $localize `Add contact to  ${this.unit.name}`;
        const message = $localize `
            Are you sure that you want to add contact to ${this.unit.name}
        `;
        this.cds.open(title,message, () => {
            this.unitSvc.addContact(this.unit, this.fieldSet.getFormValue()).subscribe ( response => {
                if (response) {
                    this.dialogRef.close(response);
                }
            }, error => this.msgSvc.showError(error, 'Changing Owner'));
        })
    }

    highlightErrors() {
        this.fields.forEach( f => {
            if (!f.control?.valid) {
                f.control.markAsTouched()
                console.log('Invalid ', f);
            }
        });
    }

    refreshEntity(entity: AbstractObject) {
        this.roleField.control.reset();
        if (entity.id !== EntityTypes.choose ) {
            this.peopleGrid.control.clear();
            this.lastNameField.control.setValidators([required]);
        } else {
            this.firstNameField.control.reset();
            this.lastNameField.control.clearValidators();
            this.lastNameField.control.reset();
            this.phoneField.control.reset();
            this.emailField.control.reset();
            this.addressField.control.reset();
            this.titleField.control.reset();
        }
        this.resetFields()
        if (entity.id === EntityTypes.individual) {
            this.showPersonFields();
        } else if (entity.id === EntityTypes.organisation){
            this.showOrgFields();
        } else {
            this.chooseFirstContact();
        }
    }

    resetFields() {
        const list = this.peopleGrid.control.value;
        if (list?.length > 0) {
            console.log('People to show', list);
            this.peopleGrid.visible = Field.showAll;
        } else {
            this.peopleGrid.visible = Field.noShow;
        }
        this.titleField.visible = Field.noShow;
        this.firstNameField.visible = Field.noShow;
        this.lastNameField.visible = Field.noShow;
        this.phoneField.visible = Field.noShow;
        this.emailField.visible = Field.noShow;
        this.addressField.visible = Field.noShow;

        this.fieldSet.formGroup.updateValueAndValidity();
    }

    showPersonFields() {
        this.lastNameField.label = $localize `Last Name`;
        this.titleField.visible = Field.showAll;
        this.firstNameField.visible = Field.showAll;
        this.lastNameField.visible = Field.showAll;
        this.phoneField.visible = Field.showAll;
        this.emailField.visible = Field.showAll;
        this.addressField.visible = Field.showAll;
    }

    showOrgFields() {
        this.lastNameField.label = $localize `Organization Name`;
        this.lastNameField.visible = Field.showAll;
        this.phoneField.visible = Field.showAll;
        this.emailField.visible = Field.showAll;
        this.addressField.visible = Field.showAll;
    }

    chooseContact() {
        return this.dialog.open(PickDialogComponent, { data: {
            config: Person.pickDialogFormConfig(),
            service: this.peopleSvc,
            showSearch: true,
            hideTabs: true,
            forceTeamId: this.unit.teamId
        }}).afterClosed().pipe(map( o => {
            console.log('Picked', o);
            if (!o) {
                return null;
            }
            if (o.primaryContactId) {
                const title = $localize `Cannot add associated contact`;
                const msg = $localize `${o.fullName} is an associated contact so cannot be added directly to unit, please choose a primary contact`;
                this.cds.alert(title, msg, this.chooseFirstContact.bind(this));
                return null;
            }
            return {id: null, personId: o.id, fullName: o.fullName}
        }));
    }

    chooseFirstContact() {
        this.chooseContact().subscribe(o => {
            console.log('Chose', o);
            if (o) {
                this.peopleGrid.control.addRow(o, true, true);
                console.log('And added ', o);
                this.resetFields();
            }
        });
    }
}
